import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { FeaturedIconOutline } from '@unified-checkout/design-system';
import {
  ArrowRight,
  CircleInfo,
} from '@unified-checkout/design-system/icons/index';
import type { TFeaturedIconOutlineColor } from 'libs/design-system/src/types/featured_icon/featured_icon_outline/featuredIconOutline';

import type { IProgressBox } from '@app/dashboard/types/innerLayout/announcement-box';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { ONBOARDING_STATES } from '@app/dashboard/constants/Home/onboarding';
import { getOnboardingBanner } from '@app/dashboard/services/inner_layout/home/onboarding';
import { AnnouncementBox } from '@app/dashboard/components/inner_layout/AnnouncementBox';

import { useOnboardingConfig } from './hooks/useOnboardingConfig';
import { hasSeenLiveBanner, setLiveBannerSeen } from './utils';

import UpdatesRequiredModal from './components/modals/UpdatesRequiredModal';

import styles from './onboarding-flow.module.scss';

export const OnboardingFlow = () => {
  const isLiveSeen = useRef(hasSeenLiveBanner()).current;

  const {
    data: result,
    error,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_ONBOARDING_BANNER],
    queryFn: getOnboardingBanner,
    select: (res) => res.data,
  });

  const bannerData = useMemo(() => {
    if (isAxiosError(error) && error.response?.data) {
      return error.response.data;
    }

    return result || null;
  }, [error, result]);

  const {
    onboardingConfig,
    activeModal,
    handleCloseModal,
    handleOpenOnboardingUrl,
  } = useOnboardingConfig({
    bannerData,
  });

  useEffect(() => {
    if (bannerData?.status === ONBOARDING_STATES.LIVE && !isLiveSeen) {
      setLiveBannerSeen();
    }
  }, [bannerData?.status]);

  if (isLiveSeen || !onboardingConfig || isLoading) return null;

  return (
    <div className={styles['onboarding-flow']}>
      <AnnouncementBox
        title={onboardingConfig.title}
        description={onboardingConfig?.subtitle}
        icon={
          <FeaturedIconOutline
            icon={CircleInfo}
            color={onboardingConfig.status as TFeaturedIconOutlineColor}
            size="lg"
          />
        }
        actionBtnsProps={
          result?.url && onboardingConfig.btnProps
            ? [
                {
                  variant: 'link',
                  size: 'md',
                  trailingIcon: ArrowRight,
                  ...onboardingConfig.btnProps,
                },
              ]
            : []
        }
        type="progress"
        status={onboardingConfig.status as IProgressBox['status']}
      />

      <UpdatesRequiredModal
        activeModal={activeModal}
        handleCloseModal={handleCloseModal}
        handleOpenOnboardingUrl={handleOpenOnboardingUrl}
        rejections={bannerData?.rejections}
      />
    </div>
  );
};
